/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { SettingsNavigationRow } from '../../components/SettingsNavigationRow';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { PlanUsageMetrics } from '../../../shell/billing/components/PlanUsageMetrics';
import { SettingsFormEditModal } from './SettingsFormEditModal';
import { PaperSheet } from '../../../shared/ticket/PaperSheet'
import { TicketButton } from '../../../shared/ticket/TicketButton';
import { BUSINESS_FUNCTIONS_HUB_ROW } from '../../domain/businessFunctionsCatalog';
import { SettingsNavListCard } from './settingsScreenLayout';

type UsageLine = { line: string; value: string; overQuota: boolean } | null;

type Props = {
  organizationNameFromApi: string | null;
  entitlementsLoadMissing: boolean;
  isOwner: boolean;
  organizationEditOpen: boolean;
  organizationNameInput: string;
  onOrganizationNameChange: (v: string) => void;
  onCloseOrganizationEdit: () => void;
  onSaveOrganizationName: () => void;
  savingOrganizationName: boolean;
  entitlementsRetrying: boolean;
  onRetryEntitlements: () => void;
  branchUsage: UsageLine;
  planName: string;
  planCodeGratis: boolean;
  showPlanKpi: boolean;
  userUsage: UsageLine;
  onOpenBilling?: () => void;
  onOpenBusinessFunctions?: () => void;
  layoutWide?: boolean;
};

export function OrganizationIdentitySection({
  organizationNameFromApi,
  entitlementsLoadMissing,
  isOwner,
  organizationEditOpen,
  organizationNameInput,
  onOrganizationNameChange,
  onCloseOrganizationEdit,
  onSaveOrganizationName,
  savingOrganizationName,
  entitlementsRetrying,
  onRetryEntitlements,
  branchUsage,
  planName,
  planCodeGratis,
  showPlanKpi,
  userUsage,
  onOpenBilling,
  onOpenBusinessFunctions,
}: Props) {
  const editModal = isOwner ? (
    <SettingsFormEditModal
      visible={organizationEditOpen}
      title="Nombre comercial"
      subtitle="Cómo aparece tu negocio en Vuelto"
      fields={[
        {
          key: 'name',
          label: 'Nombre',
          value: organizationNameInput,
          placeholder: 'Ej. Kiosco Central',
          required: true,
        },
      ]}
      saving={savingOrganizationName}
      saveLabel="Guardar"
      onClose={onCloseOrganizationEdit}
      onFieldChange={(_, value) => onOrganizationNameChange(value)}
      onSave={onSaveOrganizationName}
    />
  ) : null;

  return (
    <View style={styles.stack}>
      {/* Hints / retry */}
      {!isOwner ? (
        <Text style={styles.hint}>Solo el dueño puede editar el nombre comercial.</Text>
      ) : null}
      {entitlementsLoadMissing ? (
        <PaperSheet padded testID="organization.retry.sheet">
          <View style={styles.retryStack}>
            <Text style={styles.hint}>No pudimos sincronizar con el servidor.</Text>
            <TicketButton
              variant="ghost"
              label={entitlementsRetrying ? 'Reintentando…' : 'Reintentar'}
              onPress={onRetryEntitlements}
              disabled={entitlementsRetrying}
              testID="organization.retry"
            />
          </View>
        </PaperSheet>
      ) : !organizationNameFromApi && isOwner ? (
        <Text style={styles.hint}>Cargá el nombre desde Editar, en el encabezado.</Text>
      ) : null}

      {onOpenBusinessFunctions ? (
        <SettingsNavListCard>
          <SettingsNavigationRow
            title={BUSINESS_FUNCTIONS_HUB_ROW.title}
            description={BUSINESS_FUNCTIONS_HUB_ROW.description}
            onPress={onOpenBusinessFunctions}
            iconName="options-outline"
            showSeparator={false}
          />
        </SettingsNavListCard>
      ) : null}

      {/* Plan — solo resumen. La navegación a facturación vive en el sidebar
          ("Plan y facturación"); una fila acá duplicaba el entry point (#1463). */}
      {onOpenBilling ? (
        <PaperSheet padded testID="organization.plan.sheet">
          <View style={styles.planStack}>
            <Text style={styles.planTitle}>Plan</Text>
            <Text style={styles.planName}>{planName}</Text>
            {showPlanKpi && !entitlementsLoadMissing && (branchUsage || userUsage) ? (
              <PlanUsageMetrics branchUsage={branchUsage} userUsage={userUsage} title="Uso" />
            ) : null}
            <Text style={styles.hint}>
              {planCodeGratis
                ? '¿Querés más beneficios? Conocé las opciones en Plan y facturación, en el menú.'
                : 'Gestioná tu suscripción desde Plan y facturación, en el menú.'}
            </Text>
          </View>
        </PaperSheet>
      ) : null}

      {editModal}
    </View>
  );
}

const styles = StyleSheet.create({
  stack: {
    gap: 10,
  },
  hint: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 18,
  },
  retryStack: {
    gap: spacing.sm,
  },
  planStack: {
    gap: spacing.sm,
  },
  planTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  planName: {
    fontSize: 18,
    fontWeight: '800',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    letterSpacing: -0.3,
  },
});
